import React from "react";
import { Box, styled } from "@mui/material";
import type { HatSentiment } from "../../types";

interface SentimentButtonsProps {
  sentiment?: HatSentiment;
  onChange?: (sentiment: HatSentiment) => void;
}

const Container = styled(Box, {
  label: "SentimentButtonsContainer",
})(() => ({
  display: "flex",
  gap: 24,
  alignItems: "center",
}));

const AvatarButton = styled(Box, {
  label: "SentimentAvatarButton",
  shouldForwardProp: (prop) => prop !== "active" && prop !== "sentiment",
})<{ active?: boolean; sentiment?: string }>(({ active, sentiment }) => ({
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.2s ease",
  backgroundColor: active
    ? sentiment === "LOVE"
      ? "#FFF1EE"
      : sentiment === "LIKE"
        ? "#EBF6FE"
        : sentiment === "NOTFORME"
          ? "#FFF8EB"
          : "#f4f6f8"
    : "#f4f6f8",
  "&:hover": {
    opacity: 0.8,
    transform: "scale(1.1)",
  },
}));

// Heart icon (filled for Love)
const HeartIcon = ({ active }: { active?: boolean }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill={active ? "#EA1D76" : "none"}
    stroke={active ? "#EA1D76" : "#A5B0BA"}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// Thumbs up icon
const ThumbsUpIcon = ({ active }: { active?: boolean }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill={active ? "#078DEE" : "none"}
    stroke={active ? "#078DEE" : "#A5B0BA"}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

// Thumbs down icon (flipped thumbs up)
const ThumbsDownIcon = ({ active }: { active?: boolean }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill={active ? "#FFAB00" : "none"}
    stroke={active ? "#FFAB00" : "#A5B0BA"}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: "scaleY(-1)" }}
  >
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

export const SentimentButtons = ({
  sentiment,
  onChange,
}: SentimentButtonsProps) => {
  const handleClick = (value: HatSentiment) => {
    onChange?.(sentiment === value ? null : value);
  };

  return (
    <Container>
      <AvatarButton
        active={sentiment === "LOVE"}
        sentiment="LOVE"
        onClick={() => handleClick("LOVE")}
        aria-label="Love this career"
      >
        <HeartIcon active={sentiment === "LOVE"} />
      </AvatarButton>
      <AvatarButton
        active={sentiment === "LIKE"}
        sentiment="LIKE"
        onClick={() => handleClick("LIKE")}
        aria-label="Like this career"
      >
        <ThumbsUpIcon active={sentiment === "LIKE"} />
      </AvatarButton>
      <AvatarButton
        active={sentiment === "NOTFORME"}
        sentiment="NOTFORME"
        onClick={() => handleClick("NOTFORME")}
        aria-label="Not for me"
      >
        <ThumbsDownIcon active={sentiment === "NOTFORME"} />
      </AvatarButton>
    </Container>
  );
};
